/**
 * Dead-Lead campaign authoring wizard — Slice C.
 *
 * Four steps:
 *   1. Name + segment
 *   2. Channels (which messaging channels are available on the cadence)
 *   3. Cadence (ordered list of steps: day offset → channel → template)
 *   4. Review + save
 *
 * Save → POST /v1/dead-leads/campaigns; the new row lands in DRAFT status
 * (manual create — not auto-generated), so the consultant can edit it on
 * the campaign card and then click "Launch" when ready.
 */
import { useEffect, useMemo, useState } from 'react';
import { deadLeadsService } from '../lib/services';

const SEGMENTS = [
  ['ORPHANED',          'Orphaned',          'Rep left or lead disowned. Best with a personal-reintro tone.'],
  ['AGED',              'Aged',              '1–2 follow-ups then silence. Trigger needed — price drop, new stock.'],
  ['SERVICE_TO_SALES',  'Service → Sales',   'Customer in the service lane with positive equity or high km.'],
  ['LONG_TERM_NURTURE', 'Long-Term Nurture', 'Lease end / lifecycle event is coming up — drip until then.'],
];

const ALL_CHANNELS = [
  ['WHATSAPP', 'WhatsApp', 'Highest engagement, customer-initiated window is free.'],
  ['SMS',      'SMS',      'Fallback when WhatsApp not opted in; DNC + Spam-Act gated.'],
  ['VOICE',    'Voice',    'AI voice agent — high effort, save for warm signals only.'],
  ['EMAIL',    'Email',    'Longer-form; useful for finance offers and service reminders.'],
];

// Sensible default cadence: one step per channel, spaced out.
const DEFAULT_OFFSETS = { WHATSAPP: 0, SMS: 3, VOICE: 7, EMAIL: 14 };
const DEFAULT_TEMPLATES = {
  WHATSAPP: 'dead_lead_day0_whatsapp',
  SMS:      'dead_lead_day3_sms',
  VOICE:    'dead_lead_day7_voice',
  EMAIL:    'dead_lead_day14_email',
};

export default function CampaignWizardModal({ open, onClose, onCreated }) {
  const [step, setStep]       = useState(1);
  const [name, setName]       = useState('');
  const [segment, setSegment] = useState('AGED');
  const [channels, setChannels] = useState({
    WHATSAPP: true, SMS: true, VOICE: false, EMAIL: true,
  });
  const [cadence, setCadence] = useState([]);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);

  // Reset on open.
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setName('');
    setSegment('AGED');
    setChannels({ WHATSAPP: true, SMS: true, VOICE: false, EMAIL: true });
    setError(null);
    setBusy(false);
  }, [open]);

  // Re-seed the cadence whenever channels selection changes.
  useEffect(() => {
    const enabled = Object.entries(channels).filter(([, on]) => on).map(([k]) => k);
    setCadence(enabled.map(ch => ({
      day: DEFAULT_OFFSETS[ch] ?? 0,
      channel: ch,
      template: DEFAULT_TEMPLATES[ch] || 'default',
    })).sort((a, b) => a.day - b.day));
  }, [channels]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const canAdvanceFromStep = useMemo(() => ({
    1: name.trim().length > 0 && !!segment,
    2: Object.values(channels).some(Boolean),
    3: cadence.length > 0,
  }), [name, segment, channels, cadence]);

  if (!open) return null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        segment,
        channels: Object.entries(channels).filter(([, on]) => on).map(([k]) => k),
        cadence: {
          steps: cadence.map(s => ({
            day: Number(s.day),
            channel: s.channel,
            template: s.template || 'default',
          })),
          stop_after_days: 21,
        },
      };
      const created = await deadLeadsService.createCampaign(body);
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message
            || err?.response?.data?.error
            || err?.message
            || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const updateCadenceRow = (idx, patch) => {
    setCadence(cur => cur.map((row, i) => i === idx ? { ...row, ...patch } : row));
  };
  const removeCadenceRow = (idx) => {
    setCadence(cur => cur.filter((_, i) => i !== idx));
  };
  const addCadenceRow = () => {
    setCadence(cur => [...cur, {
      day: cur.length ? Math.max(...cur.map(r => r.day)) + 3 : 0,
      channel: 'WHATSAPP',
      template: 'default',
    }]);
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} className="modal-backdrop">
      <div className="modal" onClick={(e) => e.stopPropagation()}
           style={{ width: 'min(720px, 94vw)' }}>
        <div className="modal-header">
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>New lost-lead campaign</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              Step {step} of 4
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div style={{ display: 'flex', gap: 6, padding: '4px 16px 12px' }}>
          {[1, 2, 3, 4].map(s => (
            <div key={s} style={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              background: s <= step ? 'var(--accent)' : 'var(--border-subtle)',
            }} />
          ))}
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {step === 1 && (
            <>
              <div className="form-field">
                <label htmlFor="cw-name">Campaign name</label>
                <input id="cw-name" value={name} autoFocus
                       onChange={(e) => setName(e.target.value)}
                       placeholder="e.g. Q3 Hilux reactivation" />
              </div>
              <div className="form-field">
                <label>Segment</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {SEGMENTS.map(([key, label, desc]) => (
                    <label key={key} className={`choice-row ${segment === key ? 'active' : ''}`}
                           style={{
                             display: 'flex', alignItems: 'flex-start', gap: 10,
                             padding: 10, border: `1px solid ${segment === key ? 'var(--accent)' : 'var(--border-subtle)'}`,
                             borderRadius: 6, cursor: 'pointer',
                           }}>
                      <input type="radio" name="seg" value={key}
                             checked={segment === key}
                             onChange={() => setSegment(key)}
                             style={{ marginTop: 3 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
                        <div className="mono text-xs text-muted">{desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <div className="form-field">
              <label>Channels</label>
              <div className="mono text-xs text-muted" style={{ marginBottom: 8 }}>
                Pick the channels this campaign is allowed to send on. The cadence
                in the next step is filtered to these.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {ALL_CHANNELS.map(([key, label, desc]) => (
                  <label key={key}
                         style={{
                           display: 'flex', alignItems: 'flex-start', gap: 10,
                           padding: 10,
                           border: `1px solid ${channels[key] ? 'var(--accent)' : 'var(--border-subtle)'}`,
                           borderRadius: 6, cursor: 'pointer',
                         }}>
                    <input type="checkbox" checked={!!channels[key]}
                           onChange={(e) => setChannels(c => ({ ...c, [key]: e.target.checked }))}
                           style={{ marginTop: 3 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
                      <div className="mono text-xs text-muted">{desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="form-field">
              <label>Cadence</label>
              <div className="mono text-xs text-muted" style={{ marginBottom: 8 }}>
                Day offset is days from campaign launch. Steps must be ordered
                soonest-first (we'll re-sort on save). The dispatcher stops the
                cadence after 21 days regardless.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {cadence.map((row, idx) => (
                  <div key={idx} style={{
                    display: 'grid',
                    gridTemplateColumns: '80px 130px 1fr 40px',
                    gap: 8, alignItems: 'center',
                  }}>
                    <input type="number" min={0} max={120} value={row.day}
                           onChange={(e) => updateCadenceRow(idx, { day: Number(e.target.value) })} />
                    <select value={row.channel}
                            onChange={(e) => updateCadenceRow(idx, { channel: e.target.value })}>
                      {ALL_CHANNELS.filter(([k]) => channels[k]).map(([k, l]) => (
                        <option key={k} value={k}>{l}</option>
                      ))}
                    </select>
                    <input value={row.template}
                           onChange={(e) => updateCadenceRow(idx, { template: e.target.value })}
                           placeholder="template id" />
                    <button type="button" className="btn small"
                            onClick={() => removeCadenceRow(idx)}
                            title="Remove step">✕</button>
                  </div>
                ))}
                <button type="button" className="btn small"
                        onClick={addCadenceRow}>+ Add step</button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <div className="card-label" style={{ marginBottom: 8 }}>REVIEW</div>
              <div className="mono text-xs"
                   style={{ display: 'grid', gridTemplateColumns: '110px 1fr',
                            rowGap: 6, columnGap: 12 }}>
                <span className="text-muted">Name</span>
                <span>{name}</span>
                <span className="text-muted">Segment</span>
                <span>{SEGMENTS.find(s => s[0] === segment)?.[1] || segment}</span>
                <span className="text-muted">Channels</span>
                <span>
                  {Object.entries(channels).filter(([, on]) => on).map(([k]) => k).join(', ')}
                </span>
                <span className="text-muted">Cadence</span>
                <span>
                  {cadence.map(s => `Day ${s.day} ${s.channel}`).join(' → ')}
                </span>
              </div>
              <div className="mono text-xs text-muted" style={{ marginTop: 14 }}>
                Saving this campaign creates it in <b>DRAFT</b> status. Targets
                won't be enqueued until you launch it from the Lost Leads page.
              </div>
            </div>
          )}

          {error && (
            <div style={{
              padding: '8px 12px',
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid var(--danger)',
              borderRadius: 6,
              color: 'var(--danger)',
              fontSize: 12,
            }}>{error}</div>
          )}
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button type="button" className="btn small"
                  onClick={() => step > 1 ? setStep(step - 1) : onClose()}>
            {step > 1 ? '← Back' : 'Cancel'}
          </button>
          {step < 4 && (
            <button type="button" className="btn small primary"
                    disabled={!canAdvanceFromStep[step]}
                    onClick={() => setStep(step + 1)}>
              Next →
            </button>
          )}
          {step === 4 && (
            <button type="button" className="btn small primary"
                    disabled={busy}
                    onClick={submit}>
              {busy ? 'Saving…' : 'Create campaign'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
